/*
 * Copyright (C) 2025-2026 The Klangmotor Authors (see AUTHORS.MD)
 * SPDX-License-Identifier: AGPL-3.0-or-later
 */

package io.peekandpoke.klang.audio_be.ignitor

import io.peekandpoke.klang.audio_be.AudioBuffer
import io.peekandpoke.klang.audio_be.CrushCore
import io.peekandpoke.klang.audio_be.DistortionCore
import io.peekandpoke.klang.audio_be.DistortionShape
import io.peekandpoke.klang.audio_be.Oversampler
import io.peekandpoke.klang.audio_be.ShapingFuncs
import io.peekandpoke.klang.audio_be.effects.PhaserCore
import io.peekandpoke.klang.audio_be.filters.DEFAULT_DC_BLOCK_COEFF
import io.peekandpoke.klang.audio_be.filters.LowPassHighPassFilters
import io.peekandpoke.klang.audio_be.filters.WetDryMix
import io.peekandpoke.klang.audio_be.parseDistortionShape
import io.peekandpoke.klang.audio_be.utils.HALF_PI
import io.peekandpoke.klang.audio_be.utils.TWO_PI
import io.peekandpoke.klang.audio_be.utils.fastSin
import io.peekandpoke.klang.audio_be.utils.flushState
import io.peekandpoke.klang.audio_be.utils.nanGuard
import io.peekandpoke.klang.common.math.semitones
import kotlin.math.exp

// ═══════════════════════════════════════════════════════════════════════════════
// Distortion
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Distortion / waveshaping combinator. Processes per-sample.
 *
 * Drives the signal into a nonlinear transfer function, generating new harmonics.
 * Amount is read once per block (control rate). Bypasses when amount <= 0.
 *
 * **DC blocker is always applied** when the effect is engaged — not just for
 * asymmetric shapes (`diode`, `rectify`) that need it for correctness, but also
 * for symmetric shapes at extreme drive where any input asymmetry causes the
 * output to rail-lock toward `±1` and produce a DC bias that can damage speakers.
 *
 * **Output is bounded to ±1 by a C¹-piecewise soft cap** ([ShapingFuncs.softCap]), at the rate the stage runs: inside
 * an oversampling region the cap comes BEFORE the region's down conversion, whose half-band can overshoot it on a
 * near-square (measured up to about 1.5 on a hard-driven saw, KLANG-00214 step 4's review).
 * Below the linear-region threshold the cap is identity (clean signals
 * untouched); above, the rail-edge transients from the DC blocker's 2× HF gain
 * are smoothly compressed toward ±1 with continuous value + slope at the
 * threshold (no cliff click). The cap is per-stage so chained ignitor
 * combinators (downstream filters, mix points) see a well-behaved bounded
 * input — without it, a heavy-distort branch can dominate a mix.
 *
 * @param amount Drive intensity. 0.0 = bypass, 0.3 = warm saturation, 1.0 = heavy distortion,
 *   2.0+ = extreme. Internally: gain = 10^(amount × 1.2). Default: 0.0 (bypass).
 * @param shape Waveshaper function. Default: "soft" (tanh). Options:
 *   - **Symmetric soft:** "soft" (tanh), "gentle" (soft clip, 2× gain), "softsat" (algebraic, gentler still), "cubic", "exp" (transistor), "sineshaper" (peak-at-unity fold).
 *   - **Symmetric hard / harsh:** "hard" (clip), "zerosquare" (high-gain tanh → square), "chebyshev" (3rd-harmonic), "fold" (sin wavefold), "linearfold" (triangle wavefold).
 *   - **Asymmetric (even harmonics, DC):** "diode", "tube" (shifted-tanh), "asym" (poly), "stompbox" (diode pedal), "rectify" (full-wave).
 */
fun Ignitor.distort(amount: Ignitor, shape: String = "soft"): Ignitor =
    drive(amount).shape(shape)
// NOTE for this Ignitor-amount door: only the DRIVE half bypasses at amount <= 0 — the shaper
// keeps shaping at unity gain (tanh(1.0) = 0.76, a -2.4 dB peak squash with odd harmonics for
// full-scale input; identity only well below |x| ~ 0.3). A CONSTANT 0 through the Double
// overload below still short-circuits to a true bypass; the DSL door has always behaved like
// this chain. (Ledger W5.)
// ^ The DOORS' law, kept by decision D2 (option A, 2026-09-25): `Shape(Drive(...))`, the drive at the
// base rate, the shaper, the DC blocker and `softCap`. The fused `IgnitorDsl.Distort` node is a
// DIFFERENT law since phase 3 step 4, the voice strip's (see `fusedDistort` below): `classic()` needs
// it to rebuild the strip bit for bit. The chain here renders DerSchmetterling's guitars, Sandsturm
// and ATruthWorthLyingFor and does not change.

/**
 * Distortion / waveshaping combinator (convenience overload with fixed amount).
 *
 * @param amount Drive intensity. 0.0 = bypass, 0.3 = warm, 1.0 = heavy, 2.0+ = extreme. Default: 0.0.
 * @param shape Waveshaper function. Default: "soft". See [distort] for all options.
 */
fun Ignitor.distort(amount: Double, shape: String = "soft"): Ignitor {
    if (amount <= 0.0) return this
    return distort(ParamIgnitor("amount", amount), shape)
}

/**
 * Pre-amplification stage. Boosts signal level without waveshaping.
 *
 * Use before a shape() or distort() to control how hard the signal hits the shaper.
 * Amount is read once per block (control rate). Bypasses when amount <= 0.
 *
 * @param amount Gain boost intensity. 0.0 = bypass, 0.5 = moderate boost, 1.0 = loud,
 *   2.0+ = extreme. Internally: gain = 10^(amount × 1.2). Default: 0.0 (bypass).
 *
 * Gain without a curve: every colour belongs to `shape`, which is why there is no drive type.
 */
fun Ignitor.drive(amount: Ignitor): Ignitor =
    DriveIgnitor(upstream = this, amount = amount)

private class DriveIgnitor(
    private val upstream: Ignitor,
    private val amount: Ignitor,
) : Ignitor {
    override fun generate(buffer: AudioBuffer, freqHz: Double, ctx: IgniteContext) {
        ctx.scratchBuffers.use { work ->
            upstream.generate(work, freqHz, ctx)

            val amt = Ignitors.readParam(amount, freqHz, ctx)
            val end = ctx.windowEnd

            if (amt <= 0.0) {
                for (i in ctx.offset until end) {
                    buffer[i] = work[i]
                }
                return@use
            }

            val driveGain = DistortionCore.drive(amt)

            for (i in ctx.offset until end) {
                buffer[i] = (work[i] * driveGain)
            }
        }
    }
}

/**
 * Pre-amplification stage (convenience overload with fixed amount).
 *
 * @param amount Gain boost intensity. 0.0 = bypass, 1.0 = loud. Default: 0.0.
 */
fun Ignitor.drive(amount: Double): Ignitor {
    if (amount <= 0.0) return this
    return drive(ParamIgnitor("amount", amount))
}

/**
 * Pure waveshaping without drive. Applies a nonlinear transfer function per sample.
 *
 * Unlike [distort], this does not boost the signal before shaping — it only clips
 * whatever amplitude is already there. Use [drive] before shape() for a two-stage chain.
 *
 * **DC blocker is always applied** to guard against rail-lock when the input is
 * already heavily saturated (e.g. after `drive`). See [distort] for the rationale.
 * Output is bounded to ±1 by a C¹-piecewise soft cap ([ShapingFuncs.softCap]), identity in the linear region,
 * smooth saturation above, at the rate the stage runs (inside a region, before its down conversion). See [distort].
 *
 * @param shape Waveshaper function. Default: "soft" (tanh). See [distort] for the full
 *   list of options ("soft", "hard", "gentle", "softsat", "cubic", "exp", "sineshaper",
 *   "zerosquare", "chebyshev", "fold", "linearfold", "diode", "tube", "asym", "stompbox",
 *   "rectify").
 */
fun Ignitor.shape(shape: String = "soft"): Ignitor =
    ShapeIgnitor(upstream = this, core = DistortionCore(shape = parseDistortionShape(shape)))

/**
 * The waveshaper with the shape already resolved (from an index knob, at build): the DSL runtime's form. Inside an
 * oversampling region the build passes the DC blocker's pole for the region's rate ([dcBlockCoefficient],
 * `DistortionCore`).
 */
internal fun Ignitor.shape(
    shape: DistortionShape,
    dcBlockCoefficient: Double = DEFAULT_DC_BLOCK_COEFF,
): Ignitor = ShapeIgnitor(
    upstream = this,
    core = DistortionCore(shape = shape, dcBlockCoefficient = dcBlockCoefficient),
)

/**
 * The `Shape` node: the shaper and the DC blocker are [DistortionCore] at drive 1.0 (the gain comes from an upstream
 * `Drive` node), and the soft cap after them is this node's own (engine tidy-up step 11, audit B2.2). `x * 1.0` is
 * exact for every value (-0.0, the infinities and the denormals included; a NaN stays a NaN, which the core's guard
 * writes as 0), so the shared core renders this node's law bit for bit.
 */
private class ShapeIgnitor(
    private val upstream: Ignitor,
    private val core: DistortionCore,
) : Ignitor {

    override fun generate(buffer: AudioBuffer, freqHz: Double, ctx: IgniteContext) {
        ctx.scratchBuffers.use { work ->
            upstream.generate(work, freqHz, ctx)

            core.process(buffer = work, offset = ctx.offset, length = ctx.length, drive = 1.0)

            val end = ctx.windowEnd

            for (i in ctx.offset until end) {
                buffer[i] = ShapingFuncs.softCap(work[i])
            }
        }
    }
}

/**
 * The runtime of the fused `IgnitorDsl.Distort` node, the distort stage `classic()` builds: drive and
 * shape as ONE unit rendering the VOICE STRIP's law through [DistortionCore], the loop the strip's
 * distort ran (phase 3 step 4, decision D2 option A, 2026-09-25): the drive, the shaper, the DC
 * blocker, NO soft cap. The doors keep their own law (`distort` above).
 *
 * **D2 supersedes ledger W5's "delete it"** (maintainer, 2026-08-30), which removed an earlier fused
 * node. W5's REASON is designed out rather than reintroduced: that node bypassed per block, so a
 * MODULATED amount crossing 0 left the DC blocker holding stale state and popped
 * at every crossing. Here there is one bypass policy, and it is the build's:
 *  - a LEAF amount at or below 0 (or unset) is the build gate (`IgnitorDslRuntime`): the node is not
 *    built, which is exactly what the strip did: it skipped its stage for the note;
 *  - a MODULATED (non-leaf) amount at or below 0 is NOT a bypass: the core keeps running at UNITY drive
 *    (what the `Drive` node does there), so its state stays contiguous through the crossing. A NaN
 *    amount drives with NaN: the core's NaN guard writes 0 for every shaped sample of that block, and
 *    what leaves it is the DC blocker's decaying tail from the block before;
 *    the next finite block renders the signal again.
 *
 * @param amount the drive amount, read once per block; see [DistortionCore.drive].
 * @param shape the waveshaper, resolved at build.
 * @param dcBlockCoefficient the DC blocker's pole, the voice's 0.995 mapped to a region's rate inside one.
 */
internal fun Ignitor.fusedDistort(
    amount: Ignitor,
    shape: DistortionShape,
    dcBlockCoefficient: Double = DEFAULT_DC_BLOCK_COEFF,
): Ignitor = FusedDistortIgnitor(
    upstream = this,
    amount = amount,
    core = DistortionCore(shape = shape, dcBlockCoefficient = dcBlockCoefficient),
)

private class FusedDistortIgnitor(
    private val upstream: Ignitor,
    private val amount: Ignitor,
    private val core: DistortionCore,
) : Ignitor {
    override fun generate(buffer: AudioBuffer, freqHz: Double, ctx: IgniteContext) {
        ctx.scratchBuffers.use { work ->
            upstream.generate(work, freqHz, ctx)

            val amt = Ignitors.readParam(amount, freqHz, ctx)
            // Unity at or below 0, never a bypass: see the KDoc (W5's hazard).
            val drive = if (amt <= 0.0) 1.0 else DistortionCore.drive(amt)

            core.process(buffer = work, offset = ctx.offset, length = ctx.length, drive = drive)

            val end = ctx.windowEnd

            for (i in ctx.offset until end) {
                buffer[i] = work[i]
            }
        }
    }
}

// ResolvedShape and resolveDistortionShape() moved to audio_be/DistortionShape.kt

// ═══════════════════════════════════════════════════════════════════════════════
// BitCrush
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Bit-depth reduction (bitcrush) for lo-fi digital sound. Processes per-sample.
 *
 * The law is [CrushCore], the voice strip's crush law (phase 3
 * step 4, decision D1, 2026-09-25: FLOOR everywhere): an asymmetric `floor` quantizer,
 * `floor(x * halfLevels) / halfLevels`, clamped to `[-1, 1]`, with a DC offset of about
 * `-0.5 / halfLevels` (-0.5 at 1 bit), which moves with modulated bits: the classic crunch. A
 * NaN sample comes out as 0. Until step 4 this node rounded (a symmetric midtread quantizer),
 * up to one grid step (0.125 at 4 bits) away from the strip.
 *
 * The bit depth is read once per block (control rate). **Bypasses below 1 bit**, and at a NaN depth:
 * fewer than 2 levels means the grid step exceeds the input range entirely.
 *
 * @param bits Bit depth. Below 1.0 = bypass. 1.0 = 2 levels (extreme lo-fi),
 *   4.0 = 16 levels, 8.0 = 256 levels, 16.0 = 65536 levels (subtle).
 *   Internally: `levels = 2^bits`. Typical range: 2.0 to 8.0.
 */
fun Ignitor.crush(bits: Ignitor): Ignitor = CrushIgnitor(upstream = this, bits = bits)

private class CrushIgnitor(
    private val upstream: Ignitor,
    private val bits: Ignitor,
) : Ignitor {
    override fun generate(buffer: AudioBuffer, freqHz: Double, ctx: IgniteContext) {
        ctx.scratchBuffers.use { work ->
            upstream.generate(work, freqHz, ctx)

            val halfLevels = CrushCore.halfLevels(Ignitors.readParam(bits, freqHz, ctx))
            val end = ctx.windowEnd

            if (halfLevels == CrushCore.BYPASS) {
                for (i in ctx.offset until end) {
                    buffer[i] = work[i]
                }
                return@use
            }

            CrushCore.quantize(input = work, output = buffer, from = ctx.offset, to = end, halfLevels = halfLevels)
        }
    }
}

/**
 * Bit-depth reduction (convenience overload with a fixed depth).
 *
 * @param bits Bit depth. Below 1.0 = bypass. 4.0 = 16 levels (lo-fi),
 *   8.0 = 256 levels. Default: 0.0.
 */
fun Ignitor.crush(bits: Double): Ignitor {
    if (bits < 1.0) return this
    return crush(ParamIgnitor("bits", bits))
}

// ═══════════════════════════════════════════════════════════════════════════════
// Coarse (Sample Rate Reducer)
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Sample-rate reducer (coarse). Holds a sample value for multiple frames. Processes per-sample.
 *
 * Creates aliased, metallic artifacts by reducing the effective sample rate.
 * The factor is read once per block (control rate). Factors in (0, 1] are audibly inactive but the
 * hold clock keeps running (an exact take-every-sample copy — ledger W3: contiguity through a
 * modulated crossing); only `factor <= 0` and non-finite values take the true bypass arm, and
 * they HEAL when the factor returns. The first hold is `factor` samples (give or take one for
 * non-dyadic factors, as 1/factor accumulates in floats), like every later hold.
 *
 * @param factor Sample-hold factor. Values <= 1.0 are audibly inactive (see above).
 *   2.0 = every 2nd sample held, 4.0 = every 4th (strong aliasing), 10.0+ = extreme lo-fi.
 *   Typical range: 2.0–8.0. Default: 0.0 (inactive).
 */
private class CoarseIgnitor(
    private val upstream: Ignitor,
    private val factor: Ignitor,
) : Ignitor {
    private var lastValue: Double = 0.0

    // Bootstrapped at 1.0 — "take a sample NOW", the oversampled strip path's shape (ledger
    // W1): the old 0.0 start + `idx == 0` block latch re-armed at note-relative sample
    // `factor` for every power-of-two factor, so a block boundary landing there displaced the
    // hold grid for the REST of the note (live in ATruthWorthLyingFor's coarse(2)); it also
    // made the first hold 2x long. Both die with this bootstrap, and every coarse path in the
    // engine now anchors its grid the same way.
    private var counter: Double = 1.0

    override fun generate(buffer: AudioBuffer, freqHz: Double, ctx: IgniteContext) {
        ctx.scratchBuffers.use { work ->
            upstream.generate(work, freqHz, ctx)

            val amt = Ignitors.readParam(factor, freqHz, ctx)
            val end = ctx.windowEnd

            // Ledger W3: the guard is load-bearing only for amt <= 0 (a negative increment
            // would walk the counter down and hold forever) and for non-finite factors (a NaN
            // would poison the counter and latch DC for the note's life, an Inf would hold
            // forever; both now read as bypass and HEAL when the factor returns). For amt in
            // (0, 1] the engaged loop below already degenerates to an exact copy, so the S&H
            // clock stays contiguous through the whole authorable range and a modulated factor
            // crossing 1.0 no longer freezes the grid or replays a stale held sample.
            // NaN-guard: the !(x > 0) form is what catches NaN.
            if (!(amt > 0.0) || amt.isInfinite()) {
                for (i in ctx.offset until end) {
                    buffer[i] = work[i]
                }
                return@use
            }

            // coerceAtLeast(1.0): factors in (0, 1] mean "take every sample"; without the
            // floor the counter would grow unboundedly at increments > 1.
            val invAmt = 1.0 / amt.coerceAtLeast(1.0)

            for (i in ctx.offset until end) {
                if (counter >= 1.0) {
                    // nanGuard mirrors the retired strip's coarse: a NaN input must not latch into the
                    // held value for `factor` frames.
                    lastValue = work[i].nanGuard()
                    counter -= 1.0
                }
                buffer[i] = lastValue
                counter += invAmt
            }
        }
    }
}

fun Ignitor.coarse(factor: Ignitor): Ignitor = CoarseIgnitor(upstream = this, factor = factor)

/**
 * Sample-rate reducer (convenience overload with a fixed factor).
 *
 * @param factor Sample-hold factor. Values <= 1.0 = inactive. 4.0 = strong aliasing. Default: 0.0.
 */
fun Ignitor.coarse(factor: Double): Ignitor {
    if (factor <= 1.0) return this
    return coarse(ParamIgnitor("factor", factor))
}

// ═══════════════════════════════════════════════════════════════════════════════
// Phaser
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * 4-stage all-pass cascade phaser with sine LFO modulation. Processes per-sample.
 *
 * Sweeps a series of notch filters through the spectrum, creating the classic
 * "whooshing" or "jet" effect. All params read once per block (control rate).
 * Bypasses when wet <= 0 — the LFO clock keeps running through a bypass, so a wet gap never
 * displaces the sweep. The LFO itself is evaluated at block boundaries (see PhaserCore), so its
 * effective Nyquist is `sampleRate / (2 * blockFrames)` (~187 Hz at 48 kHz / 128); above that the
 * sweep aliases at the block rate. `rate` is deliberately unclamped (raw engine).
 *
 * @param wet Wet/dry balance under the shared C4 law (correlated branch, p = 2):
 *   0.0 = bit-exact bypass, 0.5 = equal mix, 1.0 = phased only. Typical range: 0.3–1.0. FIRST,
 *   as on the DSL door (`IgnitorDsl.phaser(wet, rate, ...)`), so a positional pair means the
 *   same thing on both Kotlin surfaces.
 * @param rate LFO speed in Hz. 0.0 = static, 0.5 = slow sweep, 2.0 = moderate,
 *   5.0+ = fast. Typical range: 0.1–5.0. Default: no default (required).
 * @param center Center frequency of the notch sweep in Hz. Default: 1000.0.
 *   Clamped to [100, 18000]. Typical range: 500–4000.
 * @param sweep Modulation width in Hz — how far the notch sweeps from center.
 *   Default: 1000.0. Clamped to [100, 18000]. Typical range: 500–3000.
 * @param floor Minimum dry coefficient in [0, 1]. Default 0.0 (true crossfade);
 *   1.0 makes the phaser purely additive like the orbit-side phaser.
 */
fun Ignitor.phaser(
    wet: Ignitor,
    rate: Ignitor,
    center: Ignitor = ParamIgnitor("center", 1000.0),
    sweep: Ignitor = ParamIgnitor("sweep", 1000.0),
    floor: Ignitor = ParamIgnitor("floor", 0.0),
): Ignitor = PhaserIgnitor(upstream = this, rate = rate, wet = wet, center = center, sweep = sweep, floor = floor)

private class PhaserIgnitor(
    private val upstream: Ignitor,
    private val rate: Ignitor,
    private val wet: Ignitor,
    private val center: Ignitor,
    private val sweep: Ignitor,
    private val floor: Ignitor,
) : Ignitor {
    // Built with the node (tidy-up step 10: nothing allocates in generate). The rate it is built at is a placeholder:
    // only `ctx.sampleRate` is the voice's, and it is bound at the first block, before the kernel is first used.
    private val core = PhaserCore(stages = PhaserCore.DEFAULT_STAGES, sampleRate = DEFAULT_BUILD_SAMPLE_RATE)
    private var rateBound = false

    // True while the cascade holds post-bypass state — cleared on bypass entry (ledger D5).
    private var stateDirty = false

    override fun generate(buffer: AudioBuffer, freqHz: Double, ctx: IgniteContext) {
        val phaser = core

        if (!rateBound) {
            rateBound = true
            phaser.bindSampleRate(ctx.sampleRate)
        }

        ctx.scratchBuffers.use { input ->
            upstream.generate(input, freqHz, ctx)

            // Read every param and advance the LFO clock UNCONDITIONALLY (ledger D1): the sweep
            // is a function of note-relative time, not of how many blocks happened to observe
            // wet > 0, and a stateful param ignitor ticks through its per-block read. The read
            // ORDER (wet, rate, center, sweep, floor) is the pre-D1 order on purpose: a
            // hand-built Kotlin graph may share one stateful instance across two slots, and the
            // slot assignment of its per-block draws is observable (review round 3). The
            // ctx.length guard on the kernel WRITES is defensive, currently unreachable: today a
            // zero-length 0.0 could never reach alphaAt anyway (prepareBlock early-returns at
            // frames <= 0, and the next real block rewrites all three first) — the guard removes
            // the reliance on those two cross-file facts, it does not fix a live bug. A param
            // cannot tick on a window with no samples, so skipping the reads there loses nothing.
            val wetVal = Ignitors.readParam(wet, freqHz, ctx).coerceIn(0.0, 1.0)

            if (ctx.length > 0) {
                phaser.rate = Ignitors.readParam(rate, freqHz, ctx)
                phaser.center = Ignitors.readParam(center, freqHz, ctx)
                phaser.sweep = Ignitors.readParam(sweep, freqHz, ctx)
            }

            phaser.prepareBlock(ctx.length)

            val floorVal = Ignitors.readParam(floor, freqHz, ctx)
            val end = ctx.windowEnd

            if (wetVal <= 0.0) {
                // Allpass state is CLEARED on bypass entry (the shimmer's C4.1 policy, ledger D5):
                // resuming on a stale cascade + feedback sample would click at an alignment-
                // dependent boundary. The LFO phase survives — PhaserCore.reset() keeps it. The
                // ctx.length guard is ledger D7: a zero-length window reads a modulated wet as 0.0
                // (deterministic E5 value) and must not get to DECIDE a bypass.
                if (stateDirty && ctx.length > 0) {
                    phaser.reset()
                    stateDirty = false
                }

                for (i in ctx.offset until end) {
                    buffer[i] = input[i]
                }
                return@use
            }

            if (ctx.length > 0) {
                stateDirty = true
            }

            // C4 (filter unification): the shared wet/dry law, correlated branch (p = 2) —
            // an allpass cascade is unit-magnitude and fully correlated, so amplitudes add.
            // floor passed RAW: WetDryMix owns the domain coercion (incl. non-finite -> 0.0),
            // so every door resolves a bad floor the SAME way (bus/strip store raw too)
            val dryC = WetDryMix.dryCoeff(wetVal, floor = floorVal, p = 2)
            val wetC = WetDryMix.wetCoeff(wetVal, p = 2)
            for (i in ctx.offset until end) {
                val dry = input[i]
                val phased = phaser.step(dry) // local name: `wet` is the Ignitor property
                buffer[i] = dry * dryC + phased * wetC
            }
        }
    }
}

/**
 * 4-stage all-pass cascade phaser (convenience overload with fixed values).
 *
 * @param wet Wet/dry balance (shared C4 law, p = 2): 0.0 = bypass, 1.0 = phased only. First and
 *   required, as on the DSL door.
 * @param rate LFO speed in Hz. Typical range: 0.1–5.0.
 * @param center Center frequency in Hz. Default: 1000.0. Clamped to [100, 18000].
 * @param sweep Modulation width in Hz. Default: 1000.0. Clamped to [100, 18000].
 * @param floor Minimum dry coefficient. Default: 0.0 (true crossfade).
 */
fun Ignitor.phaser(
    wet: Double,
    rate: Double,
    center: Double = 1000.0,
    sweep: Double = 1000.0,
    floor: Double = 0.0,
): Ignitor {
    if (wet <= 0.0) return this
    return phaser(
        wet = ParamIgnitor("wet", wet),
        rate = ParamIgnitor("rate", rate),
        center = ParamIgnitor("center", center),
        sweep = ParamIgnitor("sweep", sweep),
        floor = ParamIgnitor("floor", floor),
    )
}

// ═══════════════════════════════════════════════════════════════════════════════
// Shimmer — granular pitch-shift cloud with feedback (Aetherizer-style)
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Granular shimmer effect — short overlapping grains read back from a ring buffer at
 * pitched rates, with a feedback loop through a tone lowpass.
 *
 * `wet`, `feedback`, `tone` and `floor` are read once per block (control rate) with no
 * smoothing: a fast-modulated wet steps the mix gain at the block rate, and a modulated tone
 * switches the feedback-LPF coefficient at block boundaries — raw engine, no hidden ramps. The
 * grain machinery itself is sample-anchored (the first grain fires on the note's first sample).
 *
 * @param wet Wet/dry balance. 0.0 = bit-exact bypass regardless of feedback, 1.0 = cloud only.
 * Mix: the shared wet/dry law, equal-POWER branch (p = 1) — the pitch-shifted tail is
 * decorrelated from the dry, so powers add and the level holds across the knob.
 * @param floor Minimum dry coefficient in [0, 1]. Default 0.0 (true crossfade).
 * @param feedback Wet → grain-buffer feedback. 0.0 = single pass, 0.9 = long cascading tails.
 *   Hard-clamped to 0.95 for stability.
 * @param tone One-pole LPF cutoff (Hz) in the feedback path. Lower = darker. Clamped to [200, 16000].
 * @param pitches Semitone transpositions for grains. Each grain is assigned a pitch from this
 *   list in round-robin order. Default: `[0, 7, 12]` (root + fifth + octave). An empty list spawns no grains:
 *   only the dry plays, at its wet/dry level (silent at wet 1). A non-finite rate reads as 1.0.
 */
fun Ignitor.shimmer(
    wet: Ignitor,
    feedback: Ignitor,
    tone: Ignitor,
    pitches: List<Double> = listOf(0.0, 7.0, 12.0),
    floor: Ignitor = ParamIgnitor("floor", 0.0),
    ringFrames: Int = SHIMMER_RING_FRAMES,
): Ignitor = ShimmerIgnitor(upstream = this, wet = wet, feedback = feedback, tone = tone, pitches = pitches, floor = floor, ringFrames = ringFrames)

/**
 * The shimmer's grain ring at the base rate, 96,000 frames (2 s at 48 kHz). Inside an oversampling region the build
 * passes this times the region's factor, so the ring holds the same time.
 */
internal const val SHIMMER_RING_FRAMES: Int = 96_000

// NOTE: shimmer is WIP — internal grain bookkeeping may still change. Keep the
// per-block logic readable; revisit perf rules (audio/ref/performance.md) once
// the grain scheduler is finalised.
private class ShimmerIgnitor(
    private val upstream: Ignitor,
    private val wet: Ignitor,
    private val feedback: Ignitor,
    private val tone: Ignitor,
    pitches: List<Double>,
    private val floor: Ignitor,
    ringFrames: Int,
) : Ignitor {
    /** True once the grain engine has produced state that a bypass must clear. */
    private var stateDirty = false

    private val ringSize = ringFrames
    private val ring = AudioBuffer(ringSize)
    private var writePos: Int = 0

    private val maxGrains = 8
    private val grainActive = BooleanArray(maxGrains)
    private val grainReadPos = DoubleArray(maxGrains)
    private val grainRate = DoubleArray(maxGrains)
    private val grainElapsed = IntArray(maxGrains)
    private val grainTotal = IntArray(maxGrains)

    private val intervalRates = DoubleArray(pitches.size) {
        val rate = pitches[it].semitones()
        // NaN-guard on a value the author can write: a non-finite rate (a NaN pitch, or one past about 12288
        // semitones) reads as 1.0, the unshifted grain. A finite rate is never clamped, however large (raw Motor).
        if (rate.isFinite()) rate else 1.0
    }
    private var nextIntervalIdx: Int = 0

    private val grainsPerSecond = 12.0
    private val grainSizeSec = 0.150
    private var samplesUntilNextGrain: Int = 0

    private var feedbackTap: Double = 0.0
    private var lpfState: Double = 0.0

    override fun generate(buffer: AudioBuffer, freqHz: Double, ctx: IgniteContext) {
        ctx.scratchBuffers.use { input ->
            upstream.generate(input, freqHz, ctx)

            val wetVal = Ignitors.readParam(wet, freqHz, ctx).coerceIn(0.0, 1.0)
            val fbVal = Ignitors.readParam(feedback, freqHz, ctx).coerceIn(0.0, 0.95)
            val toneVal = Ignitors.readParam(tone, freqHz, ctx).coerceIn(200.0, 16000.0)
            // Read (tick) floor unconditionally too (the D1 rule, all four slots).
            val floorVal = Ignitors.readParam(floor, freqHz, ctx)
            val end = ctx.windowEnd

            // C4: wet == 0 IS bypass, regardless of feedback — bit-identical passthrough is
            // the wet(0) contract. State is CLEARED on bypass entry (decided in the C4.1
            // review): a modulated wet dipping to 0 must not freeze a stale tail and
            // resurrect it later, detached from wall time.
            if (wetVal <= 0.0) {
                // The ctx.length guard is ledger D7: a zero-length window reads a modulated wet
                // as 0.0 (the deterministic E5 value) and must not get to DECIDE a bypass.
                if (stateDirty && ctx.length > 0) {
                    ring.fill(0.0)
                    for (g in 0 until maxGrains) grainActive[g] = false
                    // Ledger D6: the grain SCHEDULER resets with the grain state — a resume must
                    // not keep a framing-dependent countdown remainder or round-robin position,
                    // which would shift every later grain onset (and possibly the pitch order)
                    // by where the bypass happened to land in a block. writePos resets too: a
                    // grain's lookback WRAPS around the ring seam, and after the wrap the read
                    // index is absolute — a surviving write head (itself block-quantised at
                    // bypass entry) would misalign the resumed cloud's warmup against the seam.
                    samplesUntilNextGrain = 0
                    nextIntervalIdx = 0
                    writePos = 0
                    feedbackTap = 0.0
                    lpfState = 0.0
                    stateDirty = false
                }
                for (i in ctx.offset until end) {
                    buffer[i] = input[i]
                }
                return@use
            }

            if (ctx.length > 0) {
                stateDirty = true
            }

            val sampleRate = ctx.sampleRate
            val grainPeriodSamples = (sampleRate / grainsPerSecond).toInt().coerceAtLeast(1)
            val grainTotalSamples = (sampleRate * grainSizeSec).toInt().coerceAtLeast(1)
            val invGrainTotal = 1.0 / grainTotalSamples

            val lpfA = exp(-TWO_PI * toneVal / sampleRate)
            val lpfOneMinusA = 1.0 - lpfA

            // C4 (filter unification): shared wet/dry law, DEcorrelated branch (p = 1) — the
            // pitch-shifted grain tail carries no phase relation to the dry, powers add.
            // floor passed RAW: WetDryMix owns the domain coercion (see the phaser above)
            val dryC = WetDryMix.dryCoeff(wetVal, floor = floorVal, p = 1)
            val wetC = WetDryMix.wetCoeff(wetVal, p = 1)

            for (i in ctx.offset until end) {
                val dry = input[i]

                var write = dry + feedbackTap * fbVal
                if (write > 2.0) write = 2.0 else if (write < -2.0) write = -2.0
                ring[writePos] = write
                writePos++
                if (writePos >= ringSize) writePos = 0

                // An empty `pitches` list is user input (`shimmer(0.5, 0.5, 4000, [])` on the script door, or the wire):
                // no grain ever spawns, the cloud is silent and the dry passes at its coefficient. Coerced, never an
                // index error on the audio thread (`/code-style` §21). The countdown then just keeps running down.
                if (samplesUntilNextGrain <= 0 && intervalRates.isNotEmpty()) {
                    samplesUntilNextGrain = grainPeriodSamples
                    val rate = intervalRates[nextIntervalIdx]
                    nextIntervalIdx = (nextIntervalIdx + 1) % intervalRates.size

                    var slot = -1
                    for (g in 0 until maxGrains) {
                        if (!grainActive[g]) {
                            slot = g; break
                        }
                    }
                    if (slot >= 0) {
                        // A floor-mod, not a loop: a huge rate (`2^100` from a pitch of 1200) made `start += ringSize`
                        // a no-op and the loop never exited. The rate is reduced modulo the ring first, so the product
                        // stays finite for any finite rate; the read position is the same, since the ring is circular
                        // and the grain length is whole. Bit-identical to the old loop for every rate below `ringSize`
                        // (a pitch below about 198 semitones): its additions were exact, and `% ringSize` is the
                        // identity there. Above it the rate reduction changes the rounding of the lookback, where the
                        // old loop could spin forever.
                        val reducedRate = rate % ringSize
                        val lookback = reducedRate * grainTotalSamples
                        var start = (writePos - lookback).mod(ringSize.toDouble())
                        // A tiny negative remainder plus the ring size can round up to the ring size, one past the end.
                        if (start >= ringSize) start = 0.0
                        grainReadPos[slot] = start
                        // The reduced rate too, so the read moves exactly at any finite rate and the wrap below is
                        // one subtraction (`pos + reducedRate < 2 * ringSize`).
                        grainRate[slot] = reducedRate
                        grainElapsed[slot] = 0
                        grainTotal[slot] = grainTotalSamples
                        grainActive[slot] = true
                    }
                }
                samplesUntilNextGrain--

                var wetSample = 0.0 // local name: `wet` is the Ignitor property
                for (g in 0 until maxGrains) {
                    if (!grainActive[g]) continue

                    val pos = grainReadPos[g]
                    val idx1 = pos.toInt()
                    val frac = pos - idx1
                    val idx2 = if (idx1 + 1 >= ringSize) 0 else idx1 + 1
                    val sample = ring[idx1] + frac * (ring[idx2] - ring[idx1])

                    val phase = grainElapsed[g] * invGrainTotal
                    // cos(2πp) = sin(2πp + π/2); p in [0, 1) keeps the argument inside the fold
                    val win = 0.5 - 0.5 * fastSin(TWO_PI * phase + HALF_PI)
                    wetSample += sample * win

                    var nextPos = pos + grainRate[g]
                    if (nextPos >= ringSize) nextPos -= ringSize
                    grainReadPos[g] = nextPos
                    grainElapsed[g]++
                    if (grainElapsed[g] >= grainTotal[g]) grainActive[g] = false
                }

                lpfState = (lpfOneMinusA * wetSample + lpfA * lpfState).flushState()
                feedbackTap = lpfState

                buffer[i] = (dry * dryC + wetSample * wetC)
            }
        }
    }
}

/**
 * Granular shimmer (convenience overload with fixed values).
 *
 * @param wet Wet/dry balance: 0.0 = bypass (regardless of feedback — the C4 contract),
 *   1.0 = cloud only. Default: 0.5.
 * @param feedback Cascade feedback. 0.0 = single pass, 0.9 = long tails. Default: 0.5.
 * @param tone Feedback-path LPF cutoff in Hz. Default: 4000.0.
 * @param pitches Semitone transpositions for grains. Default: `[0, 7, 12]`.
 * @param floor Minimum dry coefficient. Default: 0.0 (true crossfade).
 */
fun Ignitor.shimmer(
    wet: Double = 0.5,
    feedback: Double = 0.5,
    tone: Double = 4000.0,
    pitches: List<Double> = listOf(0.0, 7.0, 12.0),
    floor: Double = 0.0,
): Ignitor {
    if (wet <= 0.0) return this
    return shimmer(
        wet = ParamIgnitor("wet", wet),
        feedback = ParamIgnitor("feedback", feedback),
        tone = ParamIgnitor("tone", tone),
        pitches = pitches,
        floor = ParamIgnitor("floor", floor),
    )
}

// ═══════════════════════════════════════════════════════════════════════════════
// DC Blocker
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * One-pole highpass DC blocker — degenerate first-order HPF with raw pole.
 *
 * Removes DC offset accumulation from waveshaping, feedback, and asymmetric clipping.
 * Essential after distortion shapes that produce asymmetric output (diode, rectify).
 *
 * Cutoff approximation: `fc ≈ (1 − a)·fs / (2π)`. Reference points:
 *   - `coefficient = 0.995` → ~35 Hz @ 44.1k, ~38 Hz @ 48k (good for feedback paths)
 *   - `coefficient = 0.999` → ~7 Hz  @ 44.1k, ~7.6 Hz @ 48k (good for master output)
 *
 * Higher coefficient = lower cutoff = less low-frequency content removed.
 *
 * Implementation delegates to [LowPassHighPassFilters.DcBlocker]; see that class for
 * the dedup history (this used to be one of 9 inline copies before 2026-04-29).
 *
 * @param coefficient Raw IIR pole. A finite value is clamped to [0, 0.99999]; NaN/Inf falls back to 0.995.
 *   Default: 0.995.
 */
fun Ignitor.dcBlock(coefficient: Double = DEFAULT_DC_BLOCK_COEFF): Ignitor =
    DcBlockIgnitor(this, coefficient)

private class DcBlockIgnitor(
    private val upstream: Ignitor,
    coefficient: Double,
) : Ignitor {
    private val dcBlocker = LowPassHighPassFilters.DcBlocker(coefficient)

    override fun generate(buffer: AudioBuffer, freqHz: Double, ctx: IgniteContext) {
        ctx.scratchBuffers.use { input ->
            upstream.generate(input, freqHz, ctx)
            dcBlocker.process(input = input, output = buffer, offset = ctx.offset, length = ctx.length)
        }
    }
}

/**
 * This signal through an unshaped round trip of an [Oversampler] per entry of [stages] (its stage count): the PHASE
 * TWINS a `parallel` node puts on a branch that lacks those oversamplers (see [BuiltIgnitor.oversamplers]). A round
 * trip is flat in level (an all-pass), so a twin changes only the phase, by exactly what the oversampler does to a
 * branch that runs through it. The oversamplers are allocated here, at build; with no stages the signal is returned as
 * it is. Inside an oversampling region every twin starts at the region's [firstStage], as every oversampler there does.
 */
internal fun Ignitor.phaseTwins(stages: List<Int>, firstStage: Int = 0): Ignitor =
    stages.fold(this) { upstream, s -> PhaseTwinIgnitor(upstream = upstream, stages = s, firstStage = firstStage) }

private class PhaseTwinIgnitor(
    private val upstream: Ignitor,
    stages: Int,
    firstStage: Int,
) : Ignitor {
    private val oversampler = Oversampler(stages = stages, firstStage = firstStage)

    override fun generate(buffer: AudioBuffer, freqHz: Double, ctx: IgniteContext) {
        upstream.generate(buffer, freqHz, ctx)

        ctx.scratchBuffers.oversample(oversampler.factor).use { work ->
            oversampler.upsample(source = buffer, offset = ctx.offset, length = ctx.length, work = work)
            oversampler.decimate(work = work, target = buffer, offset = ctx.offset, length = ctx.length)
        }
    }
}

/**
 * This signal [frames] samples later: the pad a `parallel` node puts on a branch that is earlier than its latest one
 * when it cannot use phase twins (see [BuiltIgnitor.latencySamples]). Silence for the first [frames] samples of the voice, then the input, exactly.
 * The ring is allocated here, at build; at 0 frames the signal is returned as it is.
 */
internal fun Ignitor.delayedBy(frames: Int): Ignitor = if (frames <= 0) this else LatencyPadIgnitor(upstream = this, frames = frames)

private class LatencyPadIgnitor(
    private val upstream: Ignitor,
    frames: Int,
) : Ignitor {
    private val ring = DoubleArray(frames)
    private var pos = 0

    override fun generate(buffer: AudioBuffer, freqHz: Double, ctx: IgniteContext) {
        upstream.generate(buffer, freqHz, ctx)

        val end = ctx.windowEnd
        val size = ring.size

        for (i in ctx.offset until end) {
            val x = buffer[i]

            buffer[i] = ring[pos]
            ring[pos] = x
            pos++

            if (pos == size) {
                pos = 0
            }
        }
    }
}
